import { Link } from 'react-router-dom';
import { useStudentCycles } from '../../../api/hooks';
import { Badge, Chip, Spinner, ErrorPanel, EmptyState, PagedFooter } from '../../../components/ui';
import { usePagination } from '../../../lib/usePagination';
import { FaClipboardList } from '../../../components/ui/icons';
import { CYCLE_STATUS_TONE } from '../../../lib/cycleStatus';
import Box from './Box';
import { dayMonth } from '../lib/format';

function deltaTone(delta: number | null | undefined): 'up' | 'down' | 'neutral' {
  if (delta == null) return 'neutral';
  if (delta >= 10) return 'up';
  if (delta <= -10) return 'down';
  return 'neutral';
}

export default function RemediationCard({ id }: { id: number }) {
  const q = useStudentCycles(id);
  // Every past cycle stays on record; each is a large card, so three at a time.
  const pg = usePagination(q.data?.cycles ?? [], 3, id);
  return (
    <Box title="Remediation cycles" help="cycle">
      {q.isLoading ? <div className="flex justify-center py-6"><Spinner size="md" /></div>
        : q.isError || !q.data ? <ErrorPanel />
        : q.data.cycles.length === 0 ? <EmptyState compact icon={<FaClipboardList />} title="No remediation cycles for this student yet" />
        : (
          <ul className="space-y-3">
            {pg.pageItems.map((c) => (
              <li key={c.id} className="rounded-[14px] border border-line bg-surface p-4 text-ink">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge tone={CYCLE_STATUS_TONE[c.status]} size="sm">{c.status}</Badge>
                  <span className="font-semibold">{c.source_exam?.name ?? 'Chapter heatmap'}</span>
                  <span className="text-xs text-muted">{c.subject.name} · {dayMonth(c.created_at)}</span>
                  {c.remedial_exam && <Link className="ml-auto text-xs font-semibold text-slate underline-offset-2 hover:underline" to={`/exams/report/${c.remedial_exam.id}`}>{c.remedial_exam.name}</Link>}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                  {c.target_chapters.map((ch) => {
                    const cell = c.student_cells[String(ch.chapter_id)];
                    const b = cell?.baseline, a = cell?.after;
                    const label = a ? `${b ? b.pct.toFixed(0) : '—'}% → ${a.after_pct.toFixed(0)}% (${a.delta == null ? '—' : `${a.delta > 0 ? '+' : ''}${a.delta.toFixed(0)}`})`
                                    : `${b ? b.pct.toFixed(0) : '—'}% → —`;
                    return (
                      <div key={ch.chapter_id} className="text-xs">
                        <div className="mb-1 font-semibold text-ink">{ch.name}</div>
                        <Chip tone={deltaTone(a?.delta)}>{label}</Chip>
                      </div>
                    );
                  })}
                </div>
                {c.student_gain?.crossed_pass && <p className="mt-2 text-xs font-semibold text-success">Crossed the pass threshold.</p>}
              </li>
            ))}
          </ul>
        )}
      {pg.pageCount > 1 && <PagedFooter className="mt-3" label="Remediation cycles pagination" {...pg} onPage={pg.setPage} />}
    </Box>
  );
}
